import React, { useEffect, useState } from "react";
import { Home, Package, Settings as SettingsIcon, History, HelpCircle, Image as ImageIcon, LogOut } from "lucide-react";
import Dashboard from "./Dashboard";
import Products from "./Products";
import MainImagePicker from "./MainImagePicker";
import Settings from "./Settings";
import HistoryComponent from "./History";
import "./MainApp.css";

const MainApp = ({ storeInfo, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  // Handle hash-based navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && ['dashboard', 'products', 'primary-image', 'settings', 'history', 'help'].includes(hash)) {
        setActiveTab(hash);
      }
    };

    // Set initial tab
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    window.location.hash = tab;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'products':
        return <Products />;
      case 'primary-image':
        return <MainImagePicker />;
      case 'settings':
        return <Settings />;
      case 'history':
        return <HistoryComponent />;
      case 'help':
        return (
          <div className="container-fluid py-4">
            <div className="row justify-content-center">
              <div className="col-12 col-lg-10">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-5">
                    <h3 className="fw-bold mb-4" style={{ fontSize: '1.25rem' }}>Help & Getting Started</h3>
                    
                    <div className="row g-4">
                      <div className="col-12 col-md-6">
                        <div className="card h-100 border-0 bg-light">
                          <div className="card-body p-4">
                            <h5 className="fw-bold mb-3" style={{ fontSize: '1rem' }}>Create Your Account</h5>
                            <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                              Haven't joined AltTextify.net yet? Sign up free and generate alt text like magic!
                            </p>
                            <a 
                              href="https://alttextify.net?utm_source=ecwid&utm_medium=dl" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-primary"
                            >
                              Register Now
                            </a>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-12 col-md-6">
                        <div className="card h-100 border-0 bg-light">
                          <div className="card-body p-4">
                            <h5 className="fw-bold mb-3" style={{ fontSize: '1rem' }}>Get Your API Key</h5>
                            <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                              Copy the key. Power the magic. Get your API Key from the dashboard.
                            </p>
                            <a 
                              href="https://alttextify.net/settings/apikeys" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-primary"
                            >
                              Create API Key
                            </a>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-12 col-md-6">
                        <div className="card h-100 border-0 bg-light">
                          <div className="card-body p-4">
                            <h5 className="fw-bold mb-3" style={{ fontSize: '1rem' }}>Contact Us</h5>
                            <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                              Need help or have questions? Our team is here for you. Reach out any time and we'll respond as soon as possible.
                            </p>
                            <a 
                              href="mailto:support@alttextify.net" 
                              className="btn btn-outline-primary mb-2"
                            >
                              Email
                            </a>
                            <a 
                              target="_blank" 
                              rel="noopener noreferrer"
                              href="https://alttextify.net/contactus" 
                              className="btn btn-outline-primary mb-2 ms-3"
                            >
                              Form
                            </a>                            
                            <div className="small text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                              Or visit our <a href="/help" style={{ textDecoration: 'underline' }}>Help Page</a> for more support options.
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-12 col-md-6">
                        <div className="card h-100 border-0 bg-light">
                          <div className="card-body p-4">
                            <h5 className="fw-bold mb-3" style={{ fontSize: '1rem' }}>Frequently Asked Questions</h5>
                            <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                              Find quick answers to common questions about AltTextify features, billing, and troubleshooting.
                            </p>
                            <a 
                              href="https://alttextify.net/faq" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-outline-primary mb-2"
                            >
                              View FAQ
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="main-app">
      {/* Header */}
      <header className="app-header">
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col">
              <div className="d-flex align-items-center">
                <img 
                  src="/alttextify-logo.png" 
                  alt="AltTextify Logo" 
                  style={{ width: 200 }}
                  className="header-logo me-3"
                />
                <div>
                  {/* <h1 className="app-title mb-0">AltTextify for Ecwid</h1> */}
                  {storeInfo && (
                    <p className="store-name mb-0">{storeInfo.name}</p>
                  )}
                </div>
              </div>
            </div>
            {/* <div className="col-auto">
              <button 
                className="btn btn-outline-danger btn-sm"
                onClick={onLogout}
              >
                <LogOut size={16} className="me-1" />
                Logout
              </button>
            </div> */}
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="tab-navigation">
        <div className="container-fluid">
          <ul className="nav nav-tabs border-0">
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => handleTabClick('dashboard')}
              >
                <Home size={18} className="me-2" />
                Dashboard
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => handleTabClick('products')}
              >
                <Package size={18} className="me-2" />
                My Products
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'primary-image' ? 'active' : ''}`}
                onClick={() => handleTabClick('primary-image')}
              >
                <ImageIcon size={18} className="me-2" />
                Primary image
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => handleTabClick('settings')}
              >
                <SettingsIcon size={18} className="me-2" />
                Settings
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => handleTabClick('history')}
              >
                <History size={18} className="me-2" />
                Alt Text Manager
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'help' ? 'active' : ''}`}
                onClick={() => handleTabClick('help')}
              >
                <HelpCircle size={18} className="me-2" />
                Help
              </button>
            </li>
          </ul>
        </div>
      </nav>

      {/* Main Content */}
      <main className="main-content">
        {renderTabContent()}
      </main>
    </div>
  );
};

export default MainApp; 